import React, { useEffect, useRef, useState } from "react";
import { MapPin } from "lucide-react";
import api from "@/lib/api";
import { COUNTRIES, REKLAME_COUNTRIES, nearCities } from "@/lib/places";

const ccOf = (country) => {
  const c = [...COUNTRIES, ...(REKLAME_COUNTRIES || [])].find((x) => x.name === country);
  if (!c?.flag) return "";
  return [...c.flag].map((ch) => String.fromCharCode(ch.codePointAt(0) - 0x1f1e6 + 65)).join("").replace(/[^A-Z]/g, "").slice(0, 2);
};

function useSuggest(query, params, min) {
  const [list, setList] = useState([]);
  const key = JSON.stringify(params);
  useEffect(() => {
    if (!query || query.trim().length < min) { setList([]); return undefined; }
    const t = setTimeout(() => {
      api.get("/places/autocomplete", { params: { q: query.trim(), ...JSON.parse(key) } }).then((r) => setList(r.data || [])).catch(() => setList([]));
    }, 350);
    return () => clearTimeout(t);
  }, [query, key, min]);
  return [list, setList];
}

function Dropdown({ items, onPick, testid, render }) {
  if (!items.length) return null;
  return (
    <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-60 overflow-y-auto" data-testid={`${testid}-suggestions`}>
      {items.map((it, i) => (
        <button key={i} type="button" data-testid={`${testid}-suggestion-${i}`} onMouseDown={(e) => e.preventDefault()} onClick={() => onPick(it)}
          className="w-full text-left px-3 py-2.5 text-sm border-b border-slate-50 last:border-0 active:bg-emerald-50 flex items-start gap-2">
          <MapPin className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /><span className="min-w-0 break-words">{render(it)}</span>
        </button>
      ))}
    </div>
  );
}

export function CityInput({ country, value, onChange, cities = [], testid, className, setCoords }) {
  const [open, setOpen] = useState(false);
  const typed = useRef(false);
  const [remote, setRemote] = useSuggest(open && typed.current ? value : "", { kind: "city", cc: ccOf(country) }, 2);
  const sorted = nearCities(country, cities);
  const local = (value ? sorted.filter((c) => c.toLowerCase().includes(value.toLowerCase())) : sorted).slice(0, 8);
  const extra = remote.filter((r) => !local.some((c) => c.toLowerCase() === (r.city || "").toLowerCase())).slice(0, 5);
  const items = [...local.map((c) => ({ city: c })), ...extra];
  return (
    <div className="relative">
      <input data-testid={testid} value={value || ""} disabled={!country} autoComplete="off" placeholder={country ? "Shkruaj qytetin" : "Zgjidh shtetin"}
        onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        onChange={(e) => { typed.current = true; onChange(e.target.value); setOpen(true); }} className={className} />
      {open && <Dropdown items={items} testid={testid} render={(it) => it.city} onPick={(it) => { onChange(it.city); if (setCoords && it.lat != null) setCoords({ lat: it.lat, lng: it.lng }); setOpen(false); setRemote([]); }} />}
    </div>
  );
}

export function AddressInput({ f, setF, setCoords, testid, placeholder, className, noPostal }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [items, setItems] = useSuggest(open ? q : "", { kind: "address", cc: ccOf(f.country), city: f.city || "" }, 3);
  const pick = (it) => {
    setF((x) => ({ ...x, address: it.address || x.address, city: x.city || it.city, postal_code: it.postal_code || x.postal_code || "" }));
    if (it.lat != null && setCoords) setCoords({ lat: it.lat, lng: it.lng });
    setOpen(false); setItems([]); setQ("");
  };
  return (
    <div className="space-y-2">
      <div className="relative">
        <input data-testid={testid} value={f.address || ""} autoComplete="off" placeholder={placeholder || "Shkruaj adresën, p.sh. Kaiserstraße 20"}
          onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
          onChange={(e) => { const v = e.target.value; setF((x) => ({ ...x, address: v })); setQ(v); setOpen(true); }} className={className} />
        {open && <Dropdown items={items} testid={testid} onPick={pick} render={(it) => <><b>{it.address}</b><span className="block text-[11px] text-slate-500">{[it.postal_code, it.city, it.country].filter(Boolean).join(", ")}</span></>} />}
      </div>
      {!noPostal && <input data-testid={`${testid}-postal`} value={f.postal_code || ""} onChange={(e) => setF((x) => ({ ...x, postal_code: e.target.value }))} placeholder="Kodi postar" inputMode="text" autoCapitalize="characters" className={className} />}
    </div>
  );
}
